import { useMemo, useState } from "react";
import { useApi } from "@/lib/useApi";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader, EmptyState } from "@/components/common";
import { CalendarCheck, Plus, Trash2, CheckCircle2, Ban } from "lucide-react";
import { fDate } from "@/lib/format";
import { toast } from "sonner";

const KIND = {
  available: { label: "Tillgänglig", icon: CheckCircle2, cls: "text-emerald-700 border-emerald-300 bg-emerald-50" },
  unavailable: { label: "Otillgänglig", icon: Ban, cls: "text-slate-700 border-slate-300 bg-slate-50" },
};

export default function Tillganglighet() {
  const { user } = useAuth();
  const isManager = (user?.access_level ?? 1) >= 3 || user?.is_placement_manager;
  const [empId, setEmpId] = useState("");
  const { data: employees } = useApi(isManager && (user?.access_level ?? 1) >= 3 ? "/employees" : null);
  const q = isManager && empId ? `?employee_id=${empId}` : "";
  const { data: rows, reload } = useApi(`/placements/availability${q}`, [empId]);
  const [f, setF] = useState({ start_date: "", end_date: "", kind: "available", note: "" });

  const add = async () => {
    if (!f.start_date) return toast.error("Ange startdatum");
    if (isManager && !empId) return toast.error("Välj konsult först");
    try {
      await api.post("/placements/availability", { ...f, employee_id: isManager ? empId : undefined });
      setF({ start_date: "", end_date: "", kind: "available", note: "" });
      reload();
      toast.success("Sparad");
    } catch (e) { toast.error(apiErr(e)); }
  };
  const del = async (id) => { try { await api.delete(`/placements/availability/${id}`); reload(); } catch (e) { toast.error(apiErr(e)); } };

  const sorted = useMemo(() => [...(rows || [])].sort((a, b) => (a.start_date || "").localeCompare(b.start_date || "")), [rows]);
  const canEdit = !isManager || !!empId;

  return (
    <div className="mx-auto max-w-2xl">
      <PageHeader title="Tillgänglighet" icon={CalendarCheck}
        subtitle={isManager ? "Se och styr konsulternas tillgänglighet – perioder som är lediga att sälja in." : "Markera när du är tillgänglig eller upptagen så dina ansvariga ser det."} />

      {isManager && (user?.access_level ?? 1) >= 3 && (
        <Card className="mb-4 p-4">
          <Label>Konsult</Label>
          <Select value={empId} onValueChange={setEmpId}>
            <SelectTrigger data-testid="av-employee"><SelectValue placeholder="Välj konsult" /></SelectTrigger>
            <SelectContent>{(employees || []).map((e) => <SelectItem key={e.id} value={e.id}>{`${e.first_name || ""} ${e.last_name || ""}`.trim() || e.email}</SelectItem>)}</SelectContent>
          </Select>
        </Card>
      )}

      {canEdit && (
        <Card className="mb-4 grid gap-3 p-4 sm:grid-cols-2" data-testid="av-form">
          <div><Label>Från</Label><Input type="date" value={f.start_date} onChange={(e) => setF({ ...f, start_date: e.target.value })} data-testid="av-start" /></div>
          <div><Label>Till (valfritt)</Label><Input type="date" value={f.end_date} onChange={(e) => setF({ ...f, end_date: e.target.value })} data-testid="av-end" /></div>
          <div><Label>Typ</Label>
            <Select value={f.kind} onValueChange={(v) => setF({ ...f, kind: v })}>
              <SelectTrigger data-testid="av-kind"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(KIND).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><Label>Notering</Label><Input value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} placeholder="t.ex. semester" data-testid="av-note" /></div>
          <div className="sm:col-span-2 flex justify-end"><Button onClick={add} data-testid="av-save"><Plus className="mr-1 h-4 w-4" />Lägg till period</Button></div>
        </Card>
      )}

      <div className="space-y-2">
        {sorted.length === 0 ? <EmptyState icon={CalendarCheck} title="Inga perioder" text={isManager && !empId ? "Välj en konsult för att se och lägga till perioder." : "Lägg till en period för att göra dig tillgänglig."} /> :
          sorted.map((r) => {
            const k = KIND[r.kind] || KIND.available;
            const Icon = k.icon;
            return (
              <Card key={r.id} className={`flex items-center gap-3 border p-3 ${k.cls}`} data-testid={`av-${r.id}`}>
                <Icon className="h-4 w-4 shrink-0" />
                <div className="flex-1">
                  <div className="text-sm font-medium">{fDate(r.start_date)}{r.end_date ? ` – ${fDate(r.end_date)}` : " – tills vidare"}</div>
                  {r.note && <div className="text-xs opacity-70">{r.note}</div>}
                </div>
                <Badge variant="outline">{k.label}</Badge>
                <button onClick={() => del(r.id)} data-testid={`av-del-${r.id}`}><Trash2 className="h-4 w-4 text-red-500" /></button>
              </Card>
            );
          })}
      </div>
    </div>
  );
}
